// DEV-only WebSocket instrumentation. Proves how often the socket layer is
// exercised (subscribe / unsubscribe churn, emits, received messages).
// Active only when WS_DEBUG_ENABLED (env opt-in, never production).
import { WS_DEBUG_ENABLED } from '@lib/constants'

export type WsEventKind = 'subscribe' | 'unsubscribe' | 'emit' | 'received'

type SocketCounts = Record<WsEventKind, number>
const emptyCounts = (): SocketCounts => ({ subscribe: 0, unsubscribe: 0, emit: 0, received: 0 })

type WsLogEntry = { id: number; t: number; kind: WsEventKind; name: string; detail?: string }
type WsStatsSnapshot = {
  totals: SocketCounts
  perSocket: Record<string, SocketCounts>
  log: WsLogEntry[]
}

let totals = emptyCounts()
let perSocket: Record<string, SocketCounts> = {}
let log: WsLogEntry[] = []
let seq = 0
let snapshot: WsStatsSnapshot = { totals, perSocket, log }

const listeners = new Set<() => void>()
const notify = () => listeners.forEach((l) => l())

export const wsDebugSubscribe = (listener: () => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const getWsDebugSnapshot = (): WsStatsSnapshot => snapshot

/** Record a WebSocket-layer event. No-op in production. */
export const trackWsEvent = (kind: WsEventKind, name: string, detail?: string) => {
  if (!WS_DEBUG_ENABLED) return

  totals = { ...totals, [kind]: totals[kind] + 1 }
  const current = perSocket[name] ?? emptyCounts()
  perSocket = { ...perSocket, [name]: { ...current, [kind]: current[kind] + 1 } }
  log = [{ id: ++seq, t: Date.now(), kind, name, detail }, ...log].slice(0, 60)
  snapshot = { totals, perSocket, log }

  notify()
}

export const resetWsDebug = () => {
  totals = emptyCounts()
  perSocket = {}
  log = []
  seq = 0
  snapshot = { totals, perSocket, log }
  notify()
}
